import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Settings, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function SystemSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState({
    habilitar_whatsapp_avaliacao: false,
    link_avaliacao_loja: '',
    gerar_nota_fiscal_default: false,
    imprimir_comprovante_default: true,
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/system-config`);
      setConfigs({
        habilitar_whatsapp_avaliacao: res.data.habilitar_whatsapp_avaliacao || false,
        link_avaliacao_loja: res.data.link_avaliacao_loja || '',
        gerar_nota_fiscal_default: res.data.gerar_nota_fiscal_default || false,
        imprimir_comprovante_default: res.data.imprimir_comprovante_default !== false,
      });
    } catch (error) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setConfigs(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/system-config`, { configs });
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Settings size={28} />
        <Typography variant="h4" fontWeight={700}>
          Configurações do Sistema
        </Typography>
      </Box>

      {/* WhatsApp */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          WhatsApp - Avaliação da Loja
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Envie automaticamente uma mensagem de WhatsApp para o cliente após finalizar uma venda, 
          pedindo que avalie sua loja.
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={configs.habilitar_whatsapp_avaliacao}
              onChange={(e) => handleChange('habilitar_whatsapp_avaliacao', e.target.checked)}
              data-testid="config-whatsapp-switch"
            />
          }
          label="Habilitar envio automático de WhatsApp para avaliação"
        />

        {configs.habilitar_whatsapp_avaliacao && (
          <TextField
            fullWidth
            label="Link de Avaliação (Google, etc.)"
            placeholder="https://g.page/r/..."
            value={configs.link_avaliacao_loja}
            onChange={(e) => handleChange('link_avaliacao_loja', e.target.value)}
            sx={{ mt: 2 }}
            helperText="Cole aqui o link do Google Meu Negócio ou outro serviço de avaliação"
            data-testid="config-link-avaliacao"
          />
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          O WhatsApp só será enviado se o cliente estiver identificado na venda e tiver um telefone válido cadastrado.
        </Alert>
      </Paper>

      {/* PDV */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          PDV - Finalização de Venda
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure os valores padrão para as opções de finalização de venda no PDV.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={configs.gerar_nota_fiscal_default}
                onChange={(e) => handleChange('gerar_nota_fiscal_default', e.target.checked)}
                data-testid="config-nf-default"
              />
            }
            label="Gerar Nota Fiscal por padrão"
          />
          <FormControlLabel
            control={
              <Switch
                checked={configs.imprimir_comprovante_default}
                onChange={(e) => handleChange('imprimir_comprovante_default', e.target.checked)}
                data-testid="config-print-default"
              />
            }
            label="Imprimir comprovante por padrão"
          />
        </Box>
      </Paper>

      {/* Botão Salvar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <Save size={20} />}
          data-testid="save-config-btn"
        >
          {saving ? "Salvando..." : "Salvar Configurações"}
        </Button>
      </Box>
    </Box>
  );
}
